'use client'
import { MinusIcon, PlusIcon, MinusCircleIcon, PlusCircleIcon } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

interface QuantityProps {
  value: number
  onChange?: (val: number) => void
  disabled?: boolean
  variant?: 'primary' | 'secondary'
  minNumber?: number
  maxNumber?: number
}

export const Quantity = ({ value, onChange, disabled, variant = 'primary', minNumber = 0, maxNumber }: QuantityProps) => {
  const [newValue, setNewValue] = useState(value)

  const isSecondary = variant === 'secondary'

  useEffect(() => {
    setNewValue(value)
  }, [value])

  return (
    <div
      className={cn(
        'relative flex w-full justify-between rounded-lg border',
        disabled ? 'border-grey-200 bg-grey-100' : 'border-grey-300 bg-white',
        isSecondary && (disabled ? 'border-grey-200 bg-grey-100' : 'bg-grey-50 border-grey-200'),
      )}
    >
      <button
        title="Decrease quantity"
        className={cn(
          'flex items-center justify-center rounded-l-lg',
          isSecondary ? 'p-2 md:p-3' : 'size-12',
          !isSecondary && 'hover:bg-grey-100',
          isSecondary && 'hover:bg-transparent',
          newValue <= minNumber && 'cursor-not-allowed opacity-50',
        )}
        aria-label="Decrease quantity"
        disabled={disabled || newValue <= minNumber}
        name="decrease-quantity"
        value={newValue}
        onClick={() => {
          setNewValue(newValue - 1)
          if (onChange) onChange(newValue - 1)
        }}
      >
        {isSecondary ? (
          <MinusCircleIcon size={16} color={disabled || newValue === 0 ? 'grey-300' : 'primary-500'} />
        ) : (
          <MinusIcon size={16} color={disabled ? 'grey-400' : 'grey-700'} />
        )}
      </button>

      <input
        title="Quantity"
        disabled={disabled}
        value={newValue}
        readOnly
        className={cn('size-12 text-center text-base focus:outline-none disabled:bg-grey-100 disabled:text-grey-400', isSecondary && 'text-grey-700')}
      />

      <button
        title="Increase quantity"
        className={cn(
          'right-0 flex items-center justify-center rounded-r-lg',
          isSecondary ? 'p-2 md:p-3' : 'size-12',
          !isSecondary && 'hover:bg-grey-100',
          isSecondary && 'hover:bg-transparent',
          maxNumber === newValue && 'cursor-not-allowed opacity-50',
        )}
        aria-label="Increase quantity"
        name="increase-quantity"
        value={newValue}
        disabled={disabled || (maxNumber ? newValue >= maxNumber : false)}
        onClick={() => {
          if (maxNumber && newValue >= maxNumber) return
          setNewValue(newValue + 1)
          if (onChange) onChange(newValue + 1)
        }}
      >
        {isSecondary ? (
          <PlusCircleIcon size={16} color={disabled || (maxNumber ? newValue >= maxNumber : false) ? 'grey-300' : 'primary-500'} />
        ) : (
          <PlusIcon size={16} color={disabled ? 'grey-400' : 'grey-700'} />
        )}
      </button>
    </div>
  )
}
